/* eslint-disable @typescript-eslint/no-explicit-any */
import { assert } from '../../utils/assert.js';
import { debugCreate } from '../../utils/debug.js';
import { GamoraFoundationGlue } from './glue.js';

const debug = debugCreate({ namespace: 'gamora:foundation:glue:iterator' });

/**
 * GamoraFoundationNodeArgs
 *
 * Mandatory node arguments
 * @internal
 */
export interface GamoraFoundationGlueIteratorArgs {
  glue: GamoraFoundationGlue<any, any>;
  maxItems?: number;
}

/**
 * GamoraFoundationGlue
 *
 * Glue class
 * @internal
 */
export class GamoraFoundationGlueIterator<TEvent extends any[]> {
  protected _events: TEvent[];
  protected _maxItems: number;
  protected _glue: GamoraFoundationGlue<any, any>;
  protected _fireNext: (() => void) | null = null;
  constructor(args: GamoraFoundationGlueIteratorArgs) {
    this._events = [];
    this._glue = args.glue;
    this._maxItems = args.maxItems ?? 100;
    this._onEvent = this._onEvent.bind(this);
  }

  /**
   * Event
   *
   * Queues events for iterator
   */
  private _onEvent(...args: TEvent) {
    if (this._events.length < this._maxItems) {
      this._events.push(args);
      if (this._fireNext) {
        this._fireNext();
      }
    } else {
      debug.log(
        '(_onEvent): Too many items in iterator queue (' +
          this._events.length +
          '), dropping event.',
      );
    }
  }

  /**
   * Gets next event in queue or waits for next event
   */
  async next(): Promise<TEvent> {
    assert(
      this._fireNext === null,
      'Invalid state: Iterator is already being consumed.',
    );
    return new Promise((resolve) => {
      // Just fire the first event in the list
      if (this._events.length !== 0) {
        const event = this._events.shift();
        assert(event !== undefined, 'Invalid state: event is undefined.');
        resolve(event);
      } else {
        // Wait for next event to appear
        this._fireNext = () => {
          assert(
            this._events.length !== 0,
            'Invalid state: next fired but no events.',
          );
          const event = this._events.shift();
          assert(event !== undefined, 'Invalid state: event is undefined.');
          this._fireNext = null;
          resolve(event);
        };
      }
    });
  }

  /**
   * Listen for events
   */
  async on(namespace: string, name: string) {
    this._glue.$on(namespace, name, this._onEvent);
  }

  /**
   * No longer listen for events
   */
  async off(namespace: string, name: string) {
    this._glue.$off(namespace, name, this._onEvent);
  }
}
